import type {
  Folder,
  Node,
  Product,
  Root,
  WizardFolder,
  WizardRootFolder,
  WizardProduct,
  WizardCommissionCost,
  WizardProductAccessory,
  TroxNetcomFolder,
  TroxNetcomProduct,
  TroxNetcomNote,
  TroxNetcomFolderRoot,
  TroxNetcomGenericProduct,
  CustomProduct,
  ProductAccessory,
} from "interfaces/schemas";
import { NodeKinds } from "interfaces/enums";

export const isProduct = (node: Node): node is Product =>
  node?.kind === NodeKinds.PRODUCT;

export const isFolder = (node: Node): node is Folder =>
  node?.kind === NodeKinds.FOLDER;

export const isRoot = (node: Node): node is Root =>
  node?.kind === NodeKinds.ROOT;

export const isWizardFolder = (node: Node): node is WizardFolder =>
  node?.kind === NodeKinds.WIZARD_FOLDER;

export const isWizardRootFolder = (node: Node): node is WizardRootFolder =>
  node?.kind === NodeKinds.WIZARD_ROOT_FOLDER;

export const isWizardProduct = (node: Node): node is WizardProduct =>
  node?.kind === NodeKinds.WIZARD_PRODUCT;

export const isWizardCommissioningCost = (
  node: Node,
): node is WizardCommissionCost =>
  node?.kind === NodeKinds.WIZARD_COMMISSIONING_COST;

export const isWizardProductAccessory = (
  node: Node,
): node is WizardProductAccessory =>
  node?.kind === NodeKinds.WIZARD_PRODUCT_ACCESSORY;

export const isWizard = (
  node: Node,
): node is
  | WizardFolder
  | WizardRootFolder
  | WizardProduct
  | WizardCommissionCost
  | WizardProductAccessory =>
  isWizardFolder(node) ||
  isWizardRootFolder(node) ||
  isWizardProduct(node) ||
  isWizardCommissioningCost(node) ||
  isWizardProductAccessory(node);

export const isTroxNetcomFolder = (node: Node): node is TroxNetcomFolder =>
  node?.kind === NodeKinds.TROX_NETCOM_FOLDER;

export const isTroxNetcomProduct = (node: Node): node is TroxNetcomProduct =>
  node?.kind === NodeKinds.TROX_NETCOM_PRODUCT;

export const isTroxNetcomNote = (node: Node): node is TroxNetcomNote =>
  node?.kind === NodeKinds.TROX_NETCOM_NOTE;

export const isTroxNetcomFolderRoot = (
  node: Node,
): node is TroxNetcomFolderRoot =>
  node?.kind === NodeKinds.TROX_NETCOM_FOLDER_ROOT;

export const isTroxNetcomGenericProduct = (
  node: Node,
): node is TroxNetcomGenericProduct =>
  node?.kind === NodeKinds.TROX_NETCOM_GENERIC_PRODUCT;

export const isTroxNetcom = (
  node: Node,
): node is
  | TroxNetcomFolder
  | TroxNetcomProduct
  | TroxNetcomFolderRoot
  | TroxNetcomGenericProduct
  | TroxNetcomNote =>
  isTroxNetcomFolder(node) ||
  isTroxNetcomProduct(node) ||
  isTroxNetcomFolderRoot(node) ||
  isTroxNetcomGenericProduct(node) ||
  isTroxNetcomNote(node);

export const isCustomProduct = (node: Node): node is CustomProduct =>
  node?.kind === NodeKinds.CUSTOM_PRODUCT;

export const isProductAccessory = (node: Node): node is ProductAccessory =>
  node?.kind === NodeKinds.PRODUCT_ACCESSORY ||
  node?.kind === NodeKinds.WIZARD_PRODUCT_ACCESSORY;

export const isAnyProduct = (
  node: Node,
): node is
  | Product
  | WizardProduct
  | WizardCommissionCost
  | TroxNetcomProduct
  | CustomProduct
  | TroxNetcomNote
  | ProductAccessory
  | WizardProductAccessory =>
  isProduct(node) ||
  isWizardProduct(node) ||
  isWizardCommissioningCost(node) ||
  isTroxNetcomProduct(node) ||
  isCustomProduct(node) ||
  isTroxNetcomNote(node) ||
  isProductAccessory(node);

export const isAnyAccessory = (
  node: Node,
): node is ProductAccessory | WizardProductAccessory =>
  isProductAccessory(node) || isWizardProductAccessory(node);

export const isAnyFolder = (
  node: Node,
): node is
  | Folder
  | Root
  | WizardFolder
  | WizardRootFolder
  | TroxNetcomFolder
  | TroxNetcomFolderRoot =>
  isFolder(node) ||
  isRoot(node) ||
  isWizardFolder(node) ||
  isWizardRootFolder(node) ||
  isTroxNetcomFolder(node) ||
  isTroxNetcomFolderRoot(node);

// export const isLinkedWizardFolder = (node: Node) => {
//   return node.kind === NodeKinds.LINKED_WIZARD_FOLDER;
// }

// export const isLinkedWizardProduct = (node: Node) => {
//   return node.kind === NodeKinds.LINKED_WIZARD_PRODUCT;
// }

// export const isXcube = (node: Node) => {
//   return node.kind === NodeKinds.XCUBE;
// }

export const isLeafNode = (node: Node): boolean =>
  !("nodes" in node) || !(Array.isArray(node.nodes) && node.nodes.length > 0);
